import {
  CircularProgress,
  Divider,
  Drawer,
  IconButton
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Button } from '@vadiun/react-components';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import useChoferes from 'app/chofer/services/ChoferService';
import ExcelJS from 'exceljs';
import PageContainer from 'layout/components/PageContainer';
import PageHeader from 'layout/components/PageHeader';
import moment from 'moment';
import { useRef, useState } from 'react';
import { createTableScrollHandler } from 'utils/tableUtils';
import {
  DriverIncomeFiltersForm,
  DriverIncomeFiltersFormType
} from '../components/DriverIncomeFiltersForm';
import { DriverIncomeTable } from '../components/DriverIncomeTable';
import { useDriverIncomeRepository } from '../services/DriverIncomeRepository';

export const DriverIncomePage = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const driverRepo = useChoferes();
  const driversQuery = useSuperQuery(() =>
    driverRepo.getChoferes('?isDeleted=false')
  );
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<DriverIncomeFiltersFormType>({
    from: null,
    to: null,
    driver_ids: [],
    turnoAbierto: false
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState({ sortBy: 'today_income', sortDir: 'DESC' });

  const [isDownloading, setIsDownloading] = useState(false);

  const driverIncomeRepo = useDriverIncomeRepository();
  const driverIncomeQuery = useSuperQuery(
    () =>
      driverIncomeRepo.getIncomeReport(
        page,
        rowsPerPage,
        sort.sortBy,
        sort.sortDir,
        filters
      ),
    { showSpinner: true },
    [page, rowsPerPage, JSON.stringify(sort), JSON.stringify(filters)]
  );

  const [visibleColumns, setVisibleColumns] = useState({
    average_income_per_day: false,
    average_income_per_hour: false,
    income_in_period: false,
    worked_hours_in_period: false,
    shift_open_date_time: true,
    total_days_worked: false,
    today_income: true,
    last_shift_open: false,
    today_income_per_worked_hour: true
  });

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const result = await driverIncomeRepo.getIncomeReport(
        1,
        9999,
        sort.sortBy,
        sort.sortDir,
        filters
      );
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Promedios Choferes');

      sheet.columns = [
        { header: 'Código', key: 'code', width: 10 },
        { header: 'Nombre', key: 'name', width: 100 },
        { header: 'Unidad de Negocio', key: 'business_unit_name', width: 25 },
        { header: 'Inicio Turno', key: 'shift_open_date_time', width: 20 },
        { header: 'Total - Día Actual', key: 'today_income', width: 20 },
        {
          header: 'Por Hora - Día Actual',
          key: 'today_income_per_worked_hour',
          width: 22
        },
        { header: 'Último Turno', key: 'last_shift_open', width: 20 },
        { header: 'Días Trabajados', key: 'total_days_worked', width: 16 },
        {
          header: 'Promedio por Día',
          key: 'average_income_per_day',
          width: 18
        },
        {
          header: 'Promedio por Hora',
          key: 'average_income_per_hour',
          width: 20
        },
        { header: 'Total en Período', key: 'income_in_period', width: 18 },
        { header: 'Horas en Período', key: 'worked_hours_in_period', width: 18 }
      ];

      const borderStyle: Partial<ExcelJS.Border> = {
        style: 'thin',
        color: { argb: 'FF000000' }
      };
      const allBorders: Partial<ExcelJS.Borders> = {
        top: borderStyle,
        left: borderStyle,
        bottom: borderStyle,
        right: borderStyle
      };

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF005F9A' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.eachCell((cell) => {
        cell.border = allBorders;
      });

      result.data.forEach((row: any) => {
        const addedRow = sheet.addRow({
          code: row.code,
          name: row.name,
          business_unit_name: row.business_unit_name ?? '',
          shift_open_date_time: row.shift_open_date_time
            ? row.shift_open_date_time.format('DD/MM/YYYY HH:mm')
            : '',
          today_income: row.today_income,
          today_income_per_worked_hour: row.today_income_per_worked_hour,
          last_shift_open: row.last_shift_open
            ? moment(row.last_shift_open).format('DD/MM/YYYY HH:mm')
            : '',
          total_days_worked: row.total_days_worked,
          average_income_per_day: row.average_income_per_day,
          average_income_per_hour: row.average_income_per_hour,
          income_in_period: row.income_in_period,
          worked_hours_in_period: row.worked_hours_in_period
        });
        addedRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          cell.border = allBorders;
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `recaudacion-choferes-${moment().format(
        'DD-MM-YYYY'
      )}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  };

  const onFiltersChange = (newFilters: DriverIncomeFiltersFormType) => {
    setFilters(newFilters);
    setPage(1);
    setShowFilters(false);
  };

  const onSortChange = (direction: string | null, sortBy: string) => {
    setSort({
      sortDir: direction === 'desc' ? 'DESC' : 'ASC',
      sortBy
    });
    setPage(1);
  };

  return (
    <div ref={tableRef}>
      <PageHeader
        area="Operaciones - Información Operativa"
        title="Promedios Choferes"
        subArea="Recaudación choferes"
        toolbar={
          <div className="flex gap-2">
            <Button
              variant="light"
              onClick={handleDownloadExcel}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <CircularProgress size={16} style={{ marginRight: 6 }} />
              ) : (
                <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
              )}
              {isDownloading ? 'Descargando...' : 'Descargar informe'}
            </Button>
            <Button variant="light" onClick={() => setShowFilters(true)}>
              Filtros
            </Button>
          </div>
        }
      />
      <PageContainer>
        <DriverIncomeTable
          handlePageChange={createTableScrollHandler(tableRef)}
          onColumnsVisibleChange={setVisibleColumns}
          incomes={driverIncomeQuery.data?.data ?? []}
          total={driverIncomeQuery.data?.total ?? 0}
          page={page}
          rowsPerPage={rowsPerPage}
          visibleColumns={visibleColumns}
          onChangePage={setPage}
          onChangeRowsPerPage={(rpp) => {
            setRowsPerPage(rpp);
            setPage(1);
          }}
          onSortChange={onSortChange}
        />
      </PageContainer>
      <Drawer
        variant="persistent"
        anchor="right"
        open={showFilters}
        className="max-w-md"
      >
        <div>
          <IconButton onClick={() => setShowFilters(false)}>
            <ChevronRightIcon />
          </IconButton>
          <Divider />
          <DriverIncomeFiltersForm
            drivers={driversQuery.data ?? []}
            initialValues={filters}
            onSubmit={async (newFilters) => onFiltersChange(newFilters)}
          />
        </div>
      </Drawer>
    </div>
  );
};
